import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { categoryService } from '@/services/category.service';
import { PlusCircle, Edit, Trash2, ChevronRight, ChevronDown } from 'lucide-react';
import { useState } from 'react';
import toast from 'react-hot-toast';
import CategoryModal from '@/components/knowledge/CategoryModal';
import DeleteConfirmationModal from '@/components/ui/DeleteConfirmationModal';
import { SkeletonLoader } from '@/components/ui/SkeletonLoader';
import usePermissionRouteAccess from '@/hooks/usePermissionRouteAccess';
import useHasPermission from '@/hooks/useHasPermission';
import { CATEGORY_PERMISSION } from '@/constants/constant-permissions';
import type {
  Category,
  CategoryTreeItem,
  CreateCategoryRequest,
  UpdateCategoryRequest,
} from '@/types/category.types';

interface CategoryItemProps {
  category: CategoryTreeItem;
  onEdit: (category: CategoryTreeItem) => void;
  onDelete: (category: CategoryTreeItem) => void;
  level?: number;
  canWrite: boolean;
}

const CategoryItem = ({ category, onEdit, onDelete, level = 0, canWrite }: CategoryItemProps) => {
  const [isOpen, setIsOpen] = useState(true);
  const hasChildren = category.categories && category.categories.length > 0;

  return (
    <div style={{ marginLeft: `${level * 1}rem` }} className="sm:ml-0">
      <div className="flex items-center justify-between p-2 sm:p-3 my-1 bg-white border rounded-lg hover:bg-gray-50 transition-colors">
        <div className="flex items-center flex-1 min-w-0">
          {hasChildren && (
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="mr-2 p-1 rounded-full hover:bg-gray-200 transition-colors flex-shrink-0"
              aria-label={isOpen ? 'Collapse' : 'Expand'}
            >
              {isOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
            </button>
          )}
          <span className="font-medium text-sm sm:text-base truncate">{category.category_name}</span>
        </div>
        <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0 ml-2">
          <button
            onClick={() => canWrite && onEdit(category)}
            disabled={!canWrite}
            className="p-1.5 sm:p-2 rounded-lg hover:bg-blue-50 hover:text-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-inherit"
            title={!canWrite ? 'You do not have permission to edit categories' : 'Edit'}
            aria-label="Edit category"
          >
            <Edit size={16} className="sm:w-4 sm:h-4" />
          </button>
          <button
            onClick={() => canWrite && onDelete(category)}
            disabled={!canWrite}
            className="p-1.5 sm:p-2 rounded-lg hover:bg-red-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent"
            title={!canWrite ? 'You do not have permission to delete categories' : 'Delete'}
            aria-label="Delete category"
          >
            <Trash2 size={16} className="text-red-600 sm:w-4 sm:h-4" />
          </button>
        </div>
      </div>
      {isOpen && hasChildren && (
        <div className="border-l-2 border-gray-200 pl-2 sm:pl-4 ml-2 sm:ml-3">
          {category.categories!.map((child) => (
            <CategoryItem key={child._id} category={child} onEdit={onEdit} onDelete={onDelete} level={level + 1} canWrite={canWrite} />
          ))}
        </div>
      )}
    </div>
  );
};

export default function Categories() {
  // Permission guards
  usePermissionRouteAccess([
    CATEGORY_PERMISSION.CATEGORY_READ,
    CATEGORY_PERMISSION.CATEGORY_WRITE,
  ]);
  const canWrite = useHasPermission([CATEGORY_PERMISSION.CATEGORY_WRITE]);

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);
  const [deletingCategory, setDeletingCategory] = useState<CategoryTreeItem | null>(null);
  const [categoryToDelete, setCategoryToDelete] = useState<{ id: string; name: string } | null>(null);
  const [apiError, setApiError] = useState<{ field: string; message: string } | null>(null);
  const queryClient = useQueryClient();

  const { data: categoryData, isLoading, error } = useQuery({
    queryKey: ['categoryTree'],
    queryFn: () => categoryService.getCategoryTree(),
  });

  const { data: parentCategories } = useQuery({
    queryKey: ['parentCategories'],
    queryFn: () => categoryService.getParentCategories(),
  });

  const createCategoryMutation = useMutation({
    mutationFn: (data: CreateCategoryRequest) => categoryService.createCategory(data),
    onSuccess: () => {
      toast.success('Category created successfully!');
      queryClient.invalidateQueries({ queryKey: ['categoryTree'] });
      queryClient.invalidateQueries({ queryKey: ['parentCategories'] });
      closeCreateModal();
    },
    onError: (error: any) => {
      const apiErrorDetail = error?.error;

      if (apiErrorDetail?.error_code === 'duplicate_category_name') {
        setApiError({
          field: 'category_name',
          message: apiErrorDetail.error_description || 'This category name already exists.',
        });
      } else {
        const errorMessage =
          apiErrorDetail?.error_description ||
          error?.message ||
          'Failed to create category.';
        toast.error(errorMessage);
      }
    },
  });

  const updateCategoryMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateCategoryRequest }) => categoryService.updateCategory(id, data),
    onSuccess: () => {
      toast.success('Category updated successfully!');
      queryClient.invalidateQueries({ queryKey: ['categoryTree'] });
      queryClient.invalidateQueries({ queryKey: ['parentCategories'] });
      closeCreateModal();
    },
    onError: (error: any) => {
      const errorMessage =
        error.response?.data?.error?.error_description ||
        error.response?.data?.message ||
        error.message ||
        'Failed to update category.';
      toast.error(errorMessage);
    },
  });

  const deleteCategoryMutation = useMutation({
    mutationFn: (id: string) => categoryService.deleteCategory(id),
    onSuccess: () => {
      toast.success('Category deleted successfully!');
      queryClient.invalidateQueries({ queryKey: ['categoryTree'] });
      queryClient.invalidateQueries({ queryKey: ['parentCategories'] });
      closeDeleteModal();
    },
    onError: (error: any) => {
      const errorMessage =
        error.response?.data?.error?.error_description ||
        error.response?.data?.message ||
        error.message ||
        'Failed to delete category.';
      toast.error(errorMessage);
    },
  });

  const handleFormSubmit = (data: CreateCategoryRequest) => {
    setApiError(null);
    if (editingCategory) {
      updateCategoryMutation.mutate({ id: editingCategory._id, data });
    } else {
      createCategoryMutation.mutate(data);
    }
  };

  const handleDeleteConfirm = () => {
    if (deletingCategory) {
      deleteCategoryMutation.mutate(deletingCategory._id);
    }
  };

  const openCreateModal = async (category: CategoryTreeItem | null = null) => {
    setApiError(null);
    if (category) {
      try {
        const fullCategory = await categoryService.getCategoryById(category._id);
        setEditingCategory(fullCategory);
        setIsCreateModalOpen(true);
      } catch (err: any) {
        const errorMessage =
          err.response?.data?.error?.error_description ||
          err.response?.data?.message ||
          err.message ||
          'Failed to fetch category details.';
        toast.error(errorMessage);
      }
    } else {
      setEditingCategory(null);
      setIsCreateModalOpen(true);
    }
  };

  const closeCreateModal = () => {
    setEditingCategory(null);
    setIsCreateModalOpen(false);
    setApiError(null);
  };

  const openDeleteModal = (category: CategoryTreeItem) => {
    setCategoryToDelete({ id: category._id, name: category.category_name });
    setDeletingCategory(category);
    setIsDeleteModalOpen(true);
  };

  const closeDeleteModal = () => {
    setDeletingCategory(null);
    setCategoryToDelete(null);
    setIsDeleteModalOpen(false);
  };

  const flattenCategories = (categories: CategoryTreeItem[]): CategoryTreeItem[] => {
    let flat: CategoryTreeItem[] = [];
    if (categories) {
      categories.forEach(cat => {
        flat.push(cat);
        if (cat.categories && cat.categories.length > 0) {
          flat = flat.concat(flattenCategories(cat.categories));
        }
      });
    }
    return flat;
  };

  if (error) toast.error('Failed to load categories.');

  const categories = categoryData?.saved_category_tree || [];
  const allCategories = flattenCategories(categories);

  return (
    <>
      <div className="space-y-4 sm:space-y-6">
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-xl p-4 sm:p-6 text-white">
          <h1 className="text-xl sm:text-2xl font-bold">Category Management</h1>
          <p className="text-blue-100 text-sm sm:text-base mt-1">Organize your knowledge store with hierarchical categories.</p>
        </div>

        <div className="flex justify-end">
          <button
            onClick={() => openCreateModal()}
            disabled={!canWrite}
            className="btn btn-primary inline-flex items-center gap-2 text-sm sm:text-base disabled:opacity-50 disabled:cursor-not-allowed"
            title={!canWrite ? 'You do not have permission to manage categories' : undefined}
          >
            <PlusCircle size={18} className="sm:w-5 sm:h-5" />
            <span className="hidden xs:inline">Add Category</span>
            <span className="xs:hidden">Add</span>
          </button>
        </div>

        <div className="card p-4">
          {isLoading ? (
            <div className="space-y-3">
              {Array.from({ length: 5 }).map((_, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center space-x-3 flex-1">
                    <SkeletonLoader type="custom" className="w-6 h-6" />
                    <SkeletonLoader type="text" className="w-48 h-5" />
                  </div>
                  <div className="flex items-center gap-2">
                    <SkeletonLoader type="custom" className="w-8 h-8" />
                    <SkeletonLoader type="custom" className="w-8 h-8" />
                  </div>
                </div>
              ))}
            </div>
          ) : categories.length > 0 ? (
            categories.map((category) => (
              <CategoryItem key={category._id} category={category} onEdit={openCreateModal} onDelete={openDeleteModal} canWrite={canWrite} />
            ))
          ) : (
            <div className="text-center py-12">
              <p className="text-gray-500 text-lg">No categories found.</p>
              <p className="text-gray-400 text-sm mt-2">Create your first category to get started.</p>
            </div>
          )}
        </div>
      </div>
      <CategoryModal
        isOpen={isCreateModalOpen}
        onClose={closeCreateModal}
        onSubmit={handleFormSubmit}
        parentCategories={parentCategories as never}
        allCategories={allCategories as never}
        category={editingCategory}
        isSubmitting={createCategoryMutation.isPending || updateCategoryMutation.isPending}
        apiError={apiError as any}
      />
      <DeleteConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={closeDeleteModal}
        onConfirm={handleDeleteConfirm}
        title="Delete Category"
        description="Are you sure you want to delete this category? This action cannot be undone."
        itemName={categoryToDelete?.name}
        isLoading={deleteCategoryMutation.isPending}
      />
    </>
  );
}
